import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { CheckCircle2, CreditCard, AlertCircle } from 'lucide-react'
import { router } from '@inertiajs/react'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { FieldValues } from '@/components/applicant/FieldValues'
import type { FieldDefinition, FieldValueData } from '@/components/applicant/FieldValues'
import { StageBadge } from '@/components/shared/StageBadge'
import { ProgressStepper } from '@/components/shared/ProgressStepper'

const STAGE_KEYS = ['application', 'payment', 'filing', 'completed'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  payment: 'Awaiting Payment',
  filing: 'Filing',
  completed: 'Complete',
}

type License = { id: string; type_name: string; business_name: string }
type TypeData = { id: string; name: string; gov_fee_cents: number; service_fee_cents: number }

type RecordData = {
  id: string
  stage: string
  period_start_date: string
  period_end_date: string
  due_date: string
  overdue: boolean
  payment: {
    id: string
    service_fee_cents: number
    gov_fee_cents: number
    total_cents: number
    currency: string
    paid: boolean
    paid_at: string | null
    invoice_number: string | null
    receipt_number: string | null
  } | null
  filing: {
    id: string
    reference_number: string | null
    submitted_at: string | null
    accepted: boolean
    rejected: boolean
    pending: boolean
    rejection_reason: string | null
  } | null
  completion: { id: string; created_at: string } | null
  application_documents: { id: string; name: string; file_url: string | null; created_at: string }[]
}

type Props = {
  license: License
  record: RecordData
  type: TypeData
  fields: FieldDefinition[]
  field_values: FieldValueData[]
}

export default function LicenseRecordsShow({ license, record, type, fields, field_values }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title={`${license.type_name} - Period`} breadcrumbs={[{ label: 'Licenses', href: 'licenses' }, { label: license.type_name, href: `licenses/${license.id}` }, { label: `${record.period_start_date} - ${record.period_end_date}` }]}>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">
              {record.period_start_date} — {record.period_end_date}
            </h1>
            <StageBadge stage={record.stage} variant="license" />
          </div>
          <p className="text-sm text-muted-foreground">{license.business_name} · {license.type_name}</p>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <span className="text-muted-foreground">Due: {new Date(record.due_date).toLocaleDateString()}</span>
            {record.overdue && (
              <span className="text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Overdue
              </span>
            )}
          </div>
        </div>

        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={record.stage} />
        </div>

        <div className="space-y-6">
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Payment</h2>
              {record.payment?.paid && (
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle2 className="w-3 h-3" />Paid
                </span>
              )}
              {!record.payment && record.stage === 'application' && (
                <Button variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => router.get(`${basePath}/licenses/${license.id}/records/${record.id}/payment`)}>
                  <CreditCard className="w-3.5 h-3.5 mr-1" />Pay now
                </Button>
              )}
            </div>
            <div className="px-4 py-3">
              {record.payment ? (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service Fee</span>
                    <span>{formatCurrency(record.payment.service_fee_cents)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Gov Fee</span>
                    <span>{formatCurrency(record.payment.gov_fee_cents)}</span>
                  </div>
                  <div className="h-px bg-border my-1.5" />
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total</span>
                    <span>{formatCurrency(record.payment.total_cents)}</span>
                  </div>
                  {record.payment.invoice_number && (
                    <div className="flex justify-between text-xs text-muted-foreground pt-1">
                      <span>Invoice</span>
                      <span className="font-mono">{record.payment.invoice_number}</span>
                    </div>
                  )}
                  {record.payment.receipt_number && (
                    <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                      <span>Receipt</span>
                      <span className="font-mono">{record.payment.receipt_number}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">{formatCurrency(type.gov_fee_cents + type.service_fee_cents)} total</p>
              )}
            </div>
          </section>

          {record.filing && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Filing Status</p>
              <div className="space-y-1.5">
                {record.filing.reference_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span>{record.filing.reference_number}</span>
                  </div>
                )}
                {record.filing.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{new Date(record.filing.submitted_at).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span>
                    {record.filing.accepted && <span className="text-green-600">Accepted</span>}
                    {record.filing.rejected && <span className="text-red-600">Rejected</span>}
                    {record.filing.pending && <span className="text-blue-600">Pending</span>}
                  </span>
                </div>
                {record.filing.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {record.filing.rejection_reason}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Form Fields */}
          {fields.length > 0 && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Form Data</p>
              <FieldValues fields={fields} values={field_values} />
            </section>
          )}

          <DocumentUploader
            title="Application Documents"
            documents={record.application_documents || []}
            uploadPath={`${basePath}/licenses/${license.id}/records/${record.id}/application_documents`}
            deletePath={(docId) => `${basePath}/licenses/${license.id}/records/${record.id}/application_documents/${docId}`}
          />
        </div>
      </div>
    </DashboardLayout>
  )
}
